// The sign-in's living background (Ruben, 2 Nov 2026: "not static, dynamic,
// not invasive; something that follows my mouse, a wave, or gas"). The
// mygaz.io dot field, in MyGaz blue: a slow swell drifts across it like gas
// in a bottle, and the dots near the pointer lift and glow. One canvas,
// nothing to load; it stops with the page and pauses when the tab is hidden.
// With Reduce Motion on (Ruben's Mac) it still moves, slower and smaller.
import { useEffect, useRef } from 'react';

const GAP = 24; // px between dots, as the old static grid
const REACH = 190; // px around the pointer that the dots answer to

const rgbOf = (css: string) => {
  const m = css.trim().match(/^#?([0-9a-f]{6})$/i);
  if (!m) return '14,116,188';
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
};

export function EntryBackdrop() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const speed = calm ? 0.4 : 1;
    const lift = calm ? 0.9 : 1;
    let rgb = rgbOf(getComputedStyle(document.documentElement).getPropertyValue('--blue'));
    let w = 0, h = 0, raf = 0;
    const p = { x: -1e4, y: -1e4, tx: -1e4, ty: -1e4, on: 0 };

    const size = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = c.clientWidth; h = c.clientHeight;
      c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const move = (e: PointerEvent) => {
      const r = c.getBoundingClientRect();
      p.tx = e.clientX - r.left; p.ty = e.clientY - r.top;
      if (p.x < -1e3) { p.x = p.tx; p.y = p.ty; }
      p.on = 1;
    };
    const away = () => { p.on = 0; };
    const theme = new MutationObserver(() => { rgb = rgbOf(getComputedStyle(document.documentElement).getPropertyValue('--blue')); });

    const t0 = performance.now();
    const frame = (now: number) => {
      const t = ((now - t0) / 1000) * speed;
      p.x += (p.tx - p.x) * 0.09; p.y += (p.ty - p.y) * 0.09;
      ctx.clearRect(0, 0, w, h);
      if (p.on) {
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, REACH * 1.6);
        g.addColorStop(0, `rgba(${rgb},${0.13 * lift})`); g.addColorStop(1, `rgba(${rgb},0)`);
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      }
      for (let y = GAP / 2; y < h; y += GAP) {
        for (let x = GAP / 2; x < w; x += GAP) {
          // Two slow waves crossing: the swell drifts left to right and up.
          const a1 = Math.sin(x * 0.011 + y * 0.007 - t * 0.8) * 0.5 + 0.5;
          const a2 = Math.sin(x * 0.004 - y * 0.012 + t * 0.45) * 0.5 + 0.5;
          const s = a1 * a2;
          let alpha = 0.08 + 0.24 * s * lift;
          let r = 0.9 + 0.8 * s * lift;
          if (p.on) {
            const d = Math.hypot(x - p.x, y - p.y);
            if (d < REACH) { const k = (1 - d / REACH) ** 2; alpha += 0.55 * k * lift; r += 1.8 * k * lift; }
          }
          ctx.fillStyle = `rgba(${rgb},${alpha.toFixed(3)})`;
          ctx.beginPath(); ctx.arc(x, y, r, 0, 6.2832); ctx.fill();
        }
      }
      raf = requestAnimationFrame(frame);
    };

    size();
    addEventListener('resize', size);
    addEventListener('pointermove', move);
    document.documentElement.addEventListener('pointerleave', away);
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      removeEventListener('resize', size);
      removeEventListener('pointermove', move);
      document.documentElement.removeEventListener('pointerleave', away);
      theme.disconnect();
    };
  }, []);

  return <canvas ref={ref} className="entry-bg" aria-hidden="true" />;
}
